import { IdentifierDTO } from '@zazume/zzm-base';
import { BigDecimal, DateVO, PriceVO, RentAdvanceCalculator } from '@zazume/zzm-utils';

import { RentAdvanceProgress } from './RentAdvanceProgress';
import { Service } from './Service';

export const AfterBrokeMonthlyRentPaymentsTo = ['owner', 'organization'] as const;
export type AfterBrokeMonthlyRentPaymentTo = (typeof AfterBrokeMonthlyRentPaymentsTo)[number];

/**
 * How an existing income is linked to a PARTIAL rent advance: 'monthlyRent' splits it between the
 * advance and the owner; 'amortization' sends the whole income to the advance, nothing to the owner.
 */
export const RentAdvanceLinkTypes = ['monthlyRent', 'amortization'] as const;
export type RentAdvanceLinkType = (typeof RentAdvanceLinkTypes)[number];

interface ConstructorParams {
  afterBrokeMonthlyRentPaymentTo?: AfterBrokeMonthlyRentPaymentTo;
  executedAt?: Date;
  fee: number;
  initialCommission?: PriceVO;
  leaseContractId: IdentifierDTO;
  monthlyRent: PriceVO;
  monthsInAdvance: number;
  /**
   * Partial rent advance: the portion of the monthly rent that is NOT advanced and that the owner
   * keeps receiving every month. When present and positive the rent advance is "partial".
   */
  unadvancedMonthlyRent?: PriceVO;
  /**
   * Partial rent advance: the exact gross total amount being advanced. Only set for partial
   * advances; for full advances the gross total is derived as `monthlyRent * monthsInAdvance`.
   */
  advancedAmount?: PriceVO;
  progress: RentAdvanceProgress;
}

export class RentAdvanceService extends Service {
  afterBrokeMonthlyRentPaymentTo?: AfterBrokeMonthlyRentPaymentTo;
  fee: number;
  initialCommission?: PriceVO;
  leaseContractId: IdentifierDTO;
  monthsInAdvance: number;
  monthlyRent: PriceVO;
  unadvancedMonthlyRent?: PriceVO;
  advancedAmount?: PriceVO;
  executedAt?: Date;
  progress: RentAdvanceProgress;

  constructor(params: ConstructorParams) {
    super('RentAdvance');
    this.fee = params.fee;
    this.initialCommission = params.initialCommission;
    this.leaseContractId = params.leaseContractId;
    this.monthsInAdvance = params.monthsInAdvance;
    this.monthlyRent = params.monthlyRent;
    this.unadvancedMonthlyRent = params.unadvancedMonthlyRent;
    this.advancedAmount = params.advancedAmount;
    this.executedAt = params.executedAt;
    this.progress = params.progress;
    this.afterBrokeMonthlyRentPaymentTo = params.afterBrokeMonthlyRentPaymentTo;
  }

  static fromRaw(service: any): RentAdvanceService {
    return new RentAdvanceService({
      afterBrokeMonthlyRentPaymentTo: service.afterBrokeMonthlyRentPaymentTo,
      executedAt: service.executedAt && DateVO.fromPrimitive(service.executedAt),
      fee: service.fee,
      initialCommission: service.initialCommission && PriceVO.fromPrimitive(service.initialCommission),
      leaseContractId: service.leaseContractId,
      monthlyRent: PriceVO.fromPrimitive(service.monthlyRent),
      monthsInAdvance: service.monthsInAdvance,
      unadvancedMonthlyRent: service.unadvancedMonthlyRent && PriceVO.fromPrimitive(service.unadvancedMonthlyRent),
      advancedAmount: service.advancedAmount && PriceVO.fromPrimitive(service.advancedAmount),
      progress: new RentAdvanceProgress({
        totalFeePaid: PriceVO.fromPrimitive(service.progress.totalFeePaid),
        totalPaid: PriceVO.fromPrimitive(service.progress.totalPaid),
        incomeIds: service.progress.incomeIds
      })
    });
  }

  /**
   * Builds the shared pure calculator from this service's fields and progress. Every rent advance
   * figure is delegated to it so backend, front and web stay unified (`RentAdvanceCalculator` in
   * `@zazume/zzm-utils`). The gross advanced amount is the single source of truth: for a partial
   * advance it is the stored `advancedAmount`; for a full advance it is `monthlyRent * monthsInAdvance`.
   */
  private toCalculator(): RentAdvanceCalculator {
    return new RentAdvanceCalculator(
      {
        monthlyRent: this.monthlyRent,
        fee: this.fee,
        advancedAmount:
          this.advancedAmount ?? this.monthlyRent.multiply(BigDecimal.fromPrimitive(this.monthsInAdvance)),
        unadvancedMonthlyRent: this.unadvancedMonthlyRent,
        initialCommission: this.initialCommission
      },
      {
        totalPaid: this.progress.totalPaid,
        totalFeePaid: this.progress.totalFeePaid,
        paidRentsCount: this.progress.incomeIds.length
      }
    );
  }

  paidRentsCount(): number {
    return this.progress.incomeIds.length;
  }

  pendingMonths(): number {
    return this.toCalculator().pendingMonths();
  }

  calculateRemainingBalance(): PriceVO {
    return this.toCalculator().calculateRemainingBalance();
  }

  isEligibleForExtension(): boolean {
    return this.toCalculator().isEligibleForExtension();
  }

  isEligibleForEarlyRepayment(): boolean {
    return this.toCalculator().isEligibleForEarlyRepayment();
  }

  rentAssigment(): PriceVO {
    return this.calculateTotalRentAssigment();
  }

  isExecuted(): boolean {
    return Boolean(this.executedAt);
  }

  isPartial(): boolean {
    return this.toCalculator().isPartial();
  }

  calculateTotalMonthlyRent(): PriceVO {
    return this.toCalculator().totalMonthlyRent();
  }

  calculateTotalFee(): PriceVO {
    return this.toCalculator().calculateTotalFee();
  }

  calculateTotalRentAssigment(): PriceVO {
    return this.toCalculator().calculateTotalRentAssigment();
  }

  calculateTotalBasePaid(): PriceVO {
    return this.toCalculator().calculateTotalBasePaid();
  }

  calculateTotalBasePaidPercentage(): number {
    return this.toCalculator().calculateBasePaidPercentage().toPrimitive();
  }

  calculateTotalFeePaidPercentage(): number {
    return this.toCalculator().calculateFeePaidPercentage().toPrimitive();
  }

  isLinkedToLease(): boolean {
    return Boolean(this.leaseContractId);
  }

  canBeDeleted(): boolean {
    return !this.isLinkedToLease();
  }

  calculatePendingRentDebt(): PriceVO {
    return this.toCalculator().calculatePendingRentDebt();
  }

  calculatePendingCommissionDebt(): PriceVO {
    return this.toCalculator().calculatePendingCommissionDebt();
  }
}
